import React, { useState, useRef, useCallback, MouseEvent, useLayoutEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

export interface TableRowProps extends React.ComponentProps<'a'> {
  columns: React.ReactNode[];
  to?: string;
  handleClick?: Function;
  handleDBClick?: Function;
  withHint?: boolean;
  clickHint?: string;
  doubleClickHint?: string;
  type?: 'flex';
}

type Coordinates = {
  x: number;
  y: number;
};

const smooth = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 0.9;
    }
`;

const StyledHint = styled.span`
  position: fixed;
  background-color: rgb(255, 255, 255);
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  color: ${({ theme }) => theme.colors.LABEL};
  padding: 4px 6px;
  animation-name: ${smooth};
  animation-duration: 0.4s;
  z-index: 1;
`;

const StyledRowColumn = styled.div<{
  width: string;
}>`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  flex-grow: 1;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  padding: 17px 20px;
  overflow: hidden;
  width: ${props => props.width || 'inherit'};
`;

const StyledRow = styled.a<{
  colCount: number;
  type?: 'flex';
}>`
  display: ${props => props.type || 'grid'};
  width: 100%;  
  grid-template-columns: repeat(${props => props.colCount}, 1fr);
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.BLUE.B50};
  }
`;

const TableRowComponent = ({
  columns,
  to,
  handleClick: _handleClick,
  handleDBClick: _handleDBClick,
  clickHint,
  doubleClickHint,
  withHint = true,
}: TableRowProps) => {
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const delay: number = 200;
  const cursorSize = 16;

  const handleClick = (ev: any) => {
    ev.preventDefault();

    if (!timer) {
      setTimer(
        setTimeout(() => {
          _handleClick && _handleClick();
          setTimer(null);
        }, delay),
      );
    }
  };

  const handleDBClick = (ev: any) => {
    ev.preventDefault();

    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }

    _handleDBClick && _handleDBClick();
  };

  const styledRow = useRef<HTMLAnchorElement | null>(null);
  const delayID = useRef<NodeJS.Timeout | number>(0);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isFavouriteIcon, setIsFavouriteIcon] = useState(false);
  const { t } = useTranslation();

  const targetArea = targetRect && coordinates;

  const isWithinVerticalBounds =
    targetArea &&
    targetRect.top < coordinates.y &&
    coordinates.y < targetRect.top + targetRect.height;

  const isWithinHorizontalBounds =
    targetArea &&
    targetRect.left < coordinates.x &&
    coordinates.x < targetRect.left + targetRect.width;

  const isShowHint =
    withHint &&
    !delayID.current &&
    isWithinVerticalBounds &&
    isWithinHorizontalBounds &&
    !isFavouriteIcon;

  const clearTimer = useCallback((timerID: NodeJS.Timeout | number) => {
    if (timerID) {
      clearTimeout(timerID);
      delayID.current = 0;
    }
  }, []);

  let favouriteElement: { left: number, right: number } | undefined;

  useLayoutEffect(() => {
    favouriteElement = styledRow.current?.querySelector('#favourite')?.getBoundingClientRect();
  }, []);

  const mouseMoveHandler = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    setIsFavouriteIcon((favouriteElement?.left || 0) < e.clientX && (favouriteElement?.right || 0) > e.clientX);

    clearTimer(delayID.current);
    setCoordinates(null);
    delayID.current = setTimeout(() => {
      setCoordinates({ x: e.clientX, y: e.clientY });
      if (styledRow.current)
        setTargetRect(styledRow.current.getBoundingClientRect());
      delayID.current = 0;
    }, 400);
  }, []);

  const mouseLeaveHandler = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (delayID.current) {
      clearTimeout(delayID.current);
      delayID.current = 0;
    }
    setCoordinates(null);
    setIsFavouriteIcon(false);
  }, []);

  return (
    <StyledRow
      ref={styledRow}
      href={to}
      colCount={columns.length}
      onClick={handleClick}
      onDoubleClick={handleDBClick}
      onMouseMove={mouseMoveHandler}
      onMouseLeave={mouseLeaveHandler}
      type="flex"
    >
      {columns.map((column, index) => {
        // @ts-ignore
        const width = column?.props && column?.props['data-set-width'];
        const elementKey =
          React.isValidElement(column) && column.key != null
            ? column.key
            : index;

        return (
          <StyledRowColumn
            key={`table-row-column-${elementKey}`}
            width={width}
          >
            {column}
          </StyledRowColumn>
        )}
      )}
      {isShowHint && (
        <StyledHint
          style={{
            top: coordinates.y + cursorSize,
            left: coordinates.x + cursorSize,
          }}
        >
          {clickHint || t('openPreviewHint')}
          <br />
          {doubleClickHint || t('goToProfileHint')}
        </StyledHint>
      )}
    </StyledRow>
  );
};

export default TableRowComponent;
